import {
  Component,
  ChangeDetectionStrategy,
  computed,
  input,
  signal,
  forwardRef,
  viewChild,
  ElementRef,
  effect,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LucideAngularModule, ChevronDown } from 'lucide-angular';
import { FieldMessageComponent } from '../field-message/field-message.component';

/** Option rendered by the shared select control. */
export interface FieldOption {
  /** Submitted option value. */
  value: string;
  /** Human-readable option label. */
  label: string;
  /** Whether the option should be disabled in the native select. */
  disabled?: boolean;
}

/**
 * Form select component that integrates with Angular forms via ControlValueAccessor.
 */
@Component({
  selector: 'app-field-select',
  imports: [LucideAngularModule, FieldMessageComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldSelectComponent),
      multi: true,
    },
  ],
  templateUrl: './field-select.component.html',
  styleUrl: './field-select.component.css',
})
export class FieldSelectComponent implements ControlValueAccessor {
  /** Field label. A trailing `*` marks the field as required. */
  readonly label        = input('');
  /** Native id assigned to the select element. */
  readonly id           = input('');
  /** Placeholder option shown before a value is selected. */
  readonly placeholder  = input('Select an option');

  /** Label text with any required marker removed. */
  readonly labelText  = computed(() => this.label().replace(/\s*\*\s*$/, '').trim());
  /** Whether the label includes a required marker. */
  readonly isRequired = computed(() => this.label().includes('*'));
  /** Selectable options rendered by the component. */
  readonly options      = input<FieldOption[]>([]);
  /** Whether the field is currently invalid. */
  readonly invalid      = input(false);
  /** Validation error text. */
  readonly errorMessage = input('');
  /** Optional helper text shown below the control. */
  readonly hint         = input('');

  /** Dropdown chevron icon. */
  readonly ChevronDownIcon = ChevronDown;

  /** Current form value. */
  readonly value      = signal('');
  /** Disabled state received from Angular forms. */
  readonly isDisabled = signal(false);

  /** Native select reference used to keep the rendered value in sync. */
  private readonly selectEl = viewChild<ElementRef<HTMLSelectElement>>('selectEl');

  /** Registered Angular forms change callback. */
  private onChangeFn:  (v: string) => void = () => {};
  /** Registered Angular forms touched callback. */
  private onTouchedFn: () => void          = () => {};

  constructor() {
    // Sync value signal to native select whenever either changes
    effect(() => {
      const el  = this.selectEl();
      const val = this.value();
      if (el) el.nativeElement.value = val;
    });
  }

  /**
   * Writes an external form value into the select.
   *
   * @param v Value supplied by Angular forms.
   */
  writeValue(v: string): void {
    this.value.set(v ?? '');
  }

  /** @inheritdoc */
  registerOnChange(fn: (v: string) => void): void { this.onChangeFn = fn; }
  /** @inheritdoc */
  registerOnTouched(fn: () => void): void         { this.onTouchedFn = fn; }
  /** @inheritdoc */
  setDisabledState(d: boolean): void              { this.isDisabled.set(d); }

  /**
   * Handles native select changes and forwards them to Angular forms.
   *
   * @param event Native change event from the select.
   */
  onChange(event: Event): void {
    const val = (event.target as HTMLSelectElement).value;
    this.value.set(val);
    this.onChangeFn(val);
    this.onTouchedFn();
  }

  /**
   * Marks the control as touched when focus leaves the select.
   */
  onBlur(): void { this.onTouchedFn(); }
}
